import React, { useState, useEffect } from 'react';
import { 
  Search, 
  MapPin, 
  Star, 
  Phone, 
  Clock, 
  Globe,
  ChevronRight,
  Filter,
  Coffee,
  Utensils,
  ShoppingBag,
  Scissors,
  Car,
  Home,
  Briefcase,
  Heart,
  Tag,
  X,
  CheckCircle,
  Bed,
  Dumbbell,
  Stethoscope,
  GraduationCap,
  Wrench,
  Sparkles,
  Building2,
  Landmark,
  Cake,
  ShoppingCart,
  Film,
  Theater,
  Loader2
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { useTheme } from '../context/ThemeContext';
import { businessesApi, campaignsApi } from '../services/api';
import BusinessDetailPage from './BusinessDetailPage';
import MiniMapPreview from '../components/MiniMapPreview';

const BusinessesPage = ({ onTabChange }) => {
  const { darkMode } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [businesses, setBusinesses] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBusiness, setSelectedBusiness] = useState(null);

  // Dark mode classes
  const bgMain = darkMode ? 'bg-slate-900' : 'bg-[#f5f7fa]';
  const bgCard = darkMode ? 'bg-slate-800' : 'bg-white';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-slate-400' : 'text-gray-500';
  const borderColor = darkMode ? 'border-slate-700' : 'border-gray-200';

  const categories = [
    { id: 'all', name: 'Tümü', icon: Briefcase },
    { id: 'restaurant', name: 'Restoranlar', icon: Utensils },
    { id: 'cafe', name: 'Kafeler', icon: Coffee },
    { id: 'hotel', name: 'Oteller', icon: Bed },
    { id: 'shopping', name: 'Alışveriş', icon: ShoppingBag },
    { id: 'market', name: 'Marketler', icon: ShoppingCart },
    { id: 'cinema', name: 'Sinemalar', icon: Film },
    { id: 'theater', name: 'Tiyatrolar', icon: Theater },
    { id: 'health', name: 'Sağlık', icon: Stethoscope },
    { id: 'beauty', name: 'Güzellik', icon: Sparkles },
    { id: 'sport', name: 'Spor', icon: Dumbbell },
    { id: 'education', name: 'Eğitim', icon: GraduationCap },
    { id: 'automotive', name: 'Otomotiv', icon: Car },
    { id: 'service', name: 'Hizmetler', icon: Wrench },
    { id: 'finance', name: 'Finans', icon: Building2 },
    { id: 'government', name: 'Kamu', icon: Landmark },
    { id: 'bakery', name: 'Pastane', icon: Cake },
    { id: 'accommodation', name: 'Konaklama', icon: Home },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [businessesData, campaignsData] = await Promise.all([
          businessesApi.getAll(activeCategory !== 'all' ? activeCategory : null, searchQuery || null),
          campaignsApi.getAll(true)
        ]);
        
        // Defensive: Ensure data is array
        setBusinesses(Array.isArray(businessesData) ? businessesData : []);
        setCampaigns(Array.isArray(campaignsData) ? campaignsData : []);
        
      } catch (error) {
        console.error('Error fetching businesses:', error);
        setBusinesses([]);
        setCampaigns([]);
      } finally {
        setLoading(false);
      }
    };
    
    const debounce = setTimeout(fetchData, 300);
    return () => clearTimeout(debounce);
  }, [activeCategory, searchQuery]);

  // Defensive helper for campaigns
  const getBusinessCampaigns = (businessId) => {
    const safeCampaigns = Array.isArray(campaigns) ? campaigns : [];
    return safeCampaigns.filter(c => c.businessId === businessId);
  };

  const handleViewCampaigns = () => {
    if (onTabChange) {
      onTabChange('campaigns');
    }
  };

  // Use new BusinessDetailPage component
  if (selectedBusiness) {
    return (
      <BusinessDetailPage 
        business={selectedBusiness} 
        onBack={() => setSelectedBusiness(null)}
        onTabChange={onTabChange}
      />
    );
  }

  return (
    <div className={`min-h-full ${bgMain} pb-24`}>
      <div className="max-w-3xl mx-auto p-4">
        <h1 className={`text-2xl font-bold ${textPrimary} mb-2`}>İşletmeler</h1>
        <p className={`${textSecondary} mb-4`}>Giresun'daki yerel işletmeleri keşfedin</p>

        {/* Search */}
        <div className="relative mb-4">
          <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${textSecondary}`} />
          <Input
            type="text"
            placeholder="İşletme ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`pl-12 pr-4 py-3 h-12 rounded-xl ${borderColor} ${bgCard} shadow-sm w-full ${textPrimary}`}
          />
        </div>

        {/* Categories */}
        <div className="category-scroll flex gap-2 mb-6 overflow-x-scroll pb-3" style={{ scrollbarWidth: 'thin', scrollbarColor: '#94a3b8 #e2e8f0' }}>
          {categories.map((cat) => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all duration-200 ${
                  activeCategory === cat.id
                    ? 'bg-[#1e3a5f] text-white shadow-md'
                    : `${bgCard} ${textSecondary} ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'} border ${borderColor}`
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm font-medium">{cat.name}</span>
              </button>
            );
          })}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" />
          </div>
        )}

        {/* Business List */}
        {!loading && (
          <div className="space-y-4">
            {Array.isArray(businesses) && businesses.map((business) => {
              const businessCampaigns = getBusinessCampaigns(business.id);
              
              return (
                <div
                  key={business.id}
                  onClick={() => setSelectedBusiness(business)}
                  className={`${bgCard} rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer border ${borderColor}`}
                >
                  <div className="flex">
                    {/* Business Image */}
                    <div className="w-32 h-32 flex-shrink-0 relative">
                      <img
                        src={business.image}
                        alt={business.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://images.unsplash.com/photo-1514933651103-005eec06c04b?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80';
                        }}
                      />
                      {businessCampaigns.length > 0 && (
                        <div className="absolute top-2 left-2 px-2 py-1 bg-red-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
                          <Tag className="w-3 h-3" />
                          {businessCampaigns.length}
                        </div>
                      )}
                      {business.is_verified && (
                        <div className="absolute bottom-2 left-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                    
                    {/* Mini Map Preview */}
                    <div className="w-28 h-32 flex-shrink-0 relative hidden sm:block">
                      <MiniMapPreview 
                        latitude={business.latitude} 
                        longitude={business.longitude}
                        zoom={14}
                      />
                      <div className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-white/90 rounded text-xs font-medium text-gray-600 z-10">
                        <MapPin className="w-3 h-3 inline mr-0.5" />
                        Harita
                      </div>
                    </div>
                    
                    {/* Business Info */}
                    <div className="flex-1 p-3 min-w-0">
                      <div className="flex items-start justify-between mb-1">
                        <div className="flex items-center gap-2 truncate pr-2">
                          <h3 className={`font-semibold ${textPrimary} truncate`}>{business.name}</h3>
                          {business.is_verified && (
                            <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          )}
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 ${
                          business.isOpen 
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' 
                            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                        }`}>
                          {business.isOpen ? 'Açık' : 'Kapalı'}
                        </span>
                      </div>
                      <p className={`text-sm ${textSecondary} mb-2 line-clamp-2`}>{business.short_description || business.description}</p>
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          <span className={`text-sm font-medium ${textPrimary}`}>{business.rating}</span>
                        </div>
                        <span className={`text-sm ${textSecondary}`}>({business.reviewCount || business.review_count || 0} yorum)</span>
                        {business.price_range && (
                          <span className="text-sm text-emerald-500 font-medium">{business.price_range}</span>
                        )}
                      </div>
                      <div className={`flex items-center gap-1 text-xs ${textSecondary}`}>
                        <MapPin className="w-3 h-3 flex-shrink-0" />
                        <span className="line-clamp-1">{business.address}</span>
                      </div>
                    </div>
                    <div className="flex items-center pr-4">
                      <ChevronRight className={`w-5 h-5 ${textSecondary}`} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {Array.isArray(businesses) && businesses.length === 0 && !loading && (
          <div className="text-center py-12">
            <Briefcase className={`w-12 h-12 ${darkMode ? 'text-slate-600' : 'text-gray-300'} mx-auto mb-4`} />
            <p className={textSecondary}>İşletme bulunamadı</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessesPage;